import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Group } from "@mantine/core";
import { IconEraser } from "@tabler/icons-react";

interface SignaturePadProps {
  value?: string | null;
  onChange?: (dataUrl: string | null) => void;
  disabled?: boolean;
}

export const SignaturePad: React.FC<SignaturePadProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [drawing, setDrawing] = useState(false);
  const [hasInk, setHasInk] = useState(Boolean(value));

  // Size the canvas backing store for the device pixel ratio so strokes
  // and the captured PNG are sharp on retina displays. The CSS size stays
  // 100% × 150px; only the internal pixel buffer scales.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const cssWidth = canvas.offsetWidth || canvas.clientWidth || 700;
    const cssHeight = canvas.offsetHeight || canvas.clientHeight || 150;
    canvas.width = Math.round(cssWidth * dpr);
    canvas.height = Math.round(cssHeight * dpr);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#101820";

    if (value) {
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, cssWidth, cssHeight);
        ctx.drawImage(img, 0, 0, cssWidth, cssHeight);
      };
      img.src = value;
      setHasInk(true);
    }
  }, [value]);

  function coords(e: React.MouseEvent | React.TouchEvent): {
    x: number;
    y: number;
  } {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    if ("touches" in e) {
      const t = e.touches[0];
      return { x: t.clientX - rect.left, y: t.clientY - rect.top };
    }
    const mouse = e as React.MouseEvent;
    return { x: mouse.clientX - rect.left, y: mouse.clientY - rect.top };
  }

  const start = (e: React.MouseEvent | React.TouchEvent) => {
    if (disabled) return;
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    e.preventDefault();
    const { x, y } = coords(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setDrawing(true);
  };

  const move = (e: React.MouseEvent | React.TouchEvent) => {
    if (!drawing || disabled) return;
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    e.preventDefault();
    const { x, y } = coords(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasInk(true);
  };

  const end = () => {
    if (!drawing || disabled) return;
    setDrawing(false);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const data = canvas.toDataURL("image/png");
    onChange?.(data);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    // ctx is scaled to CSS pixels — clear using CSS dimensions, not the
    // raw (DPR-scaled) backing-store size.
    ctx.clearRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);
    setHasInk(false);
    onChange?.(null);
  };

  return (
    <Box>
      <Box
        style={{
          position: "relative",
          border: "2px dashed var(--mantine-color-gray-3)",
          borderRadius: 8,
          background: "var(--mantine-color-white)",
        }}
      >
        <canvas
          ref={canvasRef}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          style={{
            width: "100%",
            height: 150,
            display: "block",
            touchAction: "none",
            cursor: disabled ? "not-allowed" : "crosshair",
          }}
        />
        {!hasInk && (
          <Box
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              color: "var(--mantine-color-gray-5)",
              fontStyle: "italic",
              fontSize: 14,
            }}
          >
            Sign here
          </Box>
        )}
      </Box>
      {!disabled && (
        <Group justify="flex-end" mt={4}>
          <Button
            variant="subtle"
            color="gray"
            size="xs"
            leftSection={<IconEraser size={12} />}
            onClick={clear}
            disabled={!hasInk}
          >
            Clear
          </Button>
        </Group>
      )}
    </Box>
  );
};
